'use client';
import React from 'react';
import Link from 'next/link';
import { ArrowRight, Calculator, FlaskConical, Zap, Cpu, Microscope, Leaf, Telescope, BookOpen } from 'lucide-react';
import Topbar from '@/components/Topbar';
import Footer from '@/components/Footer';

const olympiads = [
  {
    id: 'olympiad-sci',
    name: 'International Science Excellence Olympiad',
    short: 'General Science',
    description: 'Broad STEM assessment covering interdisciplinary scientific concepts and experimental reasoning through scenario & logic based questions.',
    icon: Microscope,
    color: 'bg-teal-50 text-teal-600',
    border: 'border-teal-100',
    grades: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    tag: 'Most Popular',
    slug: 'science',
  },
  {
    id: 'olympiad-math',
    name: 'International Mathematics Excellence Olympiad',
    short: 'Mathematics',
    description: 'Benchmark analytical thinking, number theory, algebra, and problem-solving across real-world mathematical contexts.',
    icon: Calculator,
    color: 'bg-blue-50 text-blue-600',
    border: 'border-blue-100',
    grades: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    tag: null,
    slug: 'mathematics',
  },
  {
    id: 'olympiad-chem',
    name: 'International Chemistry Excellence Olympiad',
    short: 'Chemistry',
    description: 'Explore atomic structure, chemical reactions, and periodic trends through scenario & logic based assessment.',
    icon: FlaskConical,
    color: 'bg-green-50 text-green-600',
    border: 'border-green-100',
    grades: [6, 7, 8, 9, 10, 11, 12],
    tag: null,
    slug: 'chemistry',
  },
  {
    id: 'olympiad-phys',
    name: 'International Physics Excellence Olympiad',
    short: 'Physics',
    description: 'Assess conceptual understanding of mechanics, optics, electromagnetism, and modern physics.',
    icon: Zap,
    color: 'bg-yellow-50 text-yellow-600',
    border: 'border-yellow-100',
    grades: [6, 7, 8, 9, 10, 11, 12],
    tag: null,
    slug: 'physics',
  },
  {
    id: 'olympiad-ai',
    name: 'International Computing & AI Excellence Olympiad',
    short: 'Computing & AI',
    description: 'Test computational thinking, algorithmic reasoning, data literacy, and AI fundamentals.',
    icon: Cpu,
    color: 'bg-purple-50 text-purple-600',
    border: 'border-purple-100',
    grades: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    tag: 'New 2026',
    slug: 'computing-ai',
  },
  {
    id: 'olympiad-bio',
    name: 'International Biology Excellence Olympiad',
    short: 'Biology',
    description: 'Evaluate understanding of cell biology, genetics, ecology, and life sciences through logical reasoning.',
    icon: Leaf,
    color: 'bg-emerald-50 text-emerald-600',
    border: 'border-emerald-100',
    grades: [6, 7, 8, 9, 10, 11, 12],
    tag: null,
    slug: 'biology',
  },
  {
    id: 'olympiad-astro',
    name: 'International Astronomy Excellence Olympiad',
    short: 'Astronomy',
    description: 'Explore stellar physics, planetary science, cosmology, and space exploration concepts.',
    icon: Telescope,
    color: 'bg-indigo-50 text-indigo-600',
    border: 'border-indigo-100',
    grades: [6, 7, 8, 9, 10, 11, 12],
    tag: null,
    slug: 'astronomy',
  },
];

export default function AllOlympiadsPage() {
  return (
    <div className="min-h-screen bg-background">
      <Topbar />
      <main className="pt-16">
        {/* Hero */}
        <section className="bg-hero-gradient py-16">
          <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 text-center">
            <span className="inline-block px-3 py-1 text-xs font-semibold bg-white/10 text-white rounded-full mb-4 tracking-wide uppercase">
              2026 Season
            </span>
            <h1 className="text-4xl font-extrabold text-white mb-3">All Olympiads</h1>
            <p className="text-slate-300 text-lg max-w-2xl mx-auto">
              7 International STEM Olympiads — Grade 1 to Grade 12. Each grade has its own dedicated test.
            </p>
          </div>
        </section>

        {/* Cards */}
        <section className="py-16">
          <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {olympiads?.map((olympiad) => (
                <div
                  key={olympiad?.id}
                  className={`bg-card border ${olympiad?.border} rounded-2xl p-6 relative flex flex-col hover:shadow-lg transition-shadow`}
                >
                  {olympiad?.tag && (
                    <span className="absolute top-4 right-4 px-2 py-0.5 text-xs font-semibold bg-accent text-white rounded-full">
                      {olympiad?.tag}
                    </span>
                  )}

                  <div className={`w-12 h-12 rounded-xl ${olympiad?.color} flex items-center justify-center mb-4`}>
                    <olympiad.icon size={22} />
                  </div>

                  <h3 className="text-sm font-bold text-foreground mb-2 leading-snug pr-12">
                    {olympiad?.name}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4 flex-1">
                    {olympiad?.description}
                  </p>

                  <div className="mb-5">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Individual Grade Tests</p>
                    <div className="flex flex-wrap gap-1.5">
                      {olympiad?.grades?.map((grade) => (
                        <span key={`${olympiad?.id}-grade-${grade}`} className="px-2 py-0.5 text-xs font-medium bg-muted text-muted-foreground rounded-md">
                          Grade {grade}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-2 mt-auto">
                    <Link
                      href={`/sign-up-login-screen`}
                      className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 bg-primary text-white text-sm font-semibold rounded-xl hover:bg-primary/90 transition-all active:scale-95"
                    >
                      Register Now
                      <ArrowRight size={14} />
                    </Link>
                    <Link
                      href={`/olympiad/${olympiad?.slug}`}
                      className="flex items-center justify-center gap-1.5 px-4 py-2.5 border border-border text-sm font-semibold rounded-xl hover:bg-muted transition-colors"
                    >
                      <BookOpen size={14} />
                      Syllabus
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
